import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dfed4268-137d-4b89-bcab-122f53e08876/artifacts/k5gcf49q_BBA%20LOGO.webp";

// Custom Logo Component with clean styling
const Logo = ({ className = "h-16 w-auto" }) => (
  <div className={`relative ${className}`} style={{ aspectRatio: '1/1' }}>
    <img 
      src={LOGO_URL} 
      alt="Bodies By Akeem" 
      className="h-full w-auto rounded-full"
      style={{ 
        background: 'transparent',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
      }}
    />
  </div>
);

const benefits = [
  'AI-Powered Workout Programs',
  'Personalized Nutrition Plans',
  'Progress Tracking & Analytics',
  'Community Support',
  '1-1 Trainer Access'
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(user.onboarding_completed ? '/dashboard' : '/onboarding');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData);
      toast.success('Account created successfully!');
      navigate('/onboarding');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left Side - Image & Benefits */}
      <div className="hidden lg:flex flex-1 relative flex-col justify-center p-12">
        <img 
          src="https://images.unsplash.com/photo-1700784795176-7ff886439d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxpbnRlbnNlJTIwZ3ltJTIwd29ya291dCUyMGF0aGxldGUlMjBkYXJrJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMzk5NTY2fDA&ixlib=rb-4.1.0&q=85"
          alt="Athlete"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] to-transparent"></div>
        
        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-4xl text-white uppercase mb-6">
            START YOUR <span className="text-primary">TRANSFORMATION</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join thousands of athletes who have already transformed their bodies with Bodies By Akeem.
          </p>
          
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-gray-300">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center mb-8">
            <Logo className="h-16" />
          </Link>
          
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl text-white uppercase mb-2">Create Account</h1>
            <p className="text-gray-400">Start your 7-day free trial</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-gray-300">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  data-testid="register-firstname-input"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="input-tactical w-full bg-secondary border-transparent"
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-gray-300">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  data-testid="register-lastname-input"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="input-tactical w-full bg-secondary border-transparent"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={handleChange}
                className="input-tactical w-full bg-secondary border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="register-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  className="input-tactical w-full bg-secondary border-transparent pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Minimum 6 characters</p>
            </div>
            
            <div className="flex items-start gap-2">
              <input 
                type="checkbox" 
                id="terms"
                required
                className="mt-1 w-4 h-4 rounded border-gray-600 bg-secondary text-primary focus:ring-primary" 
              />
              <label htmlFor="terms" className="text-sm text-gray-400">
                I agree to the{' '}
                <a href="#" className="text-primary hover:text-red-400">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:text-red-400">Privacy Policy</a>
              </label>
            </div>
            
            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold py-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          <p className="text-center mt-6 text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-red-400 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
